export const ORGS_WITH_GRAVITI_PORTAL_FEATURES = ['Graviti', 'ENERGONLABS'];

const getComparableOrgId = (orgId) => String(orgId || '').trim().toLowerCase();

export const usesGravitiPortalFeatures = (orgId) => (
  ORGS_WITH_GRAVITI_PORTAL_FEATURES.map(getComparableOrgId).includes(getComparableOrgId(orgId))
);

// Pilot rollout of the tags / attributes feature (card chips, Add/Edit form Tags section,
// dashboard filter + group-by, and the Tag Management page). Limited to these orgs for the
// initial week of testing. TO OPEN IT TO EVERYONE: add org ids here, or replace the body of
// usesTagsFeature with `return true;` — nothing else needs to change.
export const ORGS_WITH_TAGS_FEATURE = ['Kims', 'demo1'];

export const usesTagsFeature = (orgId) => (
  ORGS_WITH_TAGS_FEATURE.map(getComparableOrgId).includes(getComparableOrgId(orgId))
);

// Platform super-admins: treated as superuser of EVERY organization (a silent default — never shown in
// a user list and not granted via Cognito/DB). Mirrors the backend, which injects Superuser for these
// accounts in whatever org they open. Compared case-insensitively; a trailing '.' (Cognito username
// form) is tolerated so either the email or the username matches.
export const PLATFORM_SUPER_ADMIN_EMAILS = ['surya@alomindlabs.com', 'surya@mindlabs.cloud'];

export const isPlatformSuperAdmin = (email) => (
  PLATFORM_SUPER_ADMIN_EMAILS.map(getComparableOrgId).includes(getComparableOrgId(email).replace(/\.$/, ''))
);

// Exact Graviti-org identity, matched by org id OR display name ('Graviti' / 'Gravity Pharma').
// Deliberately narrower than usesGravitiPortalFeatures, whose list also includes ENERGONLABS — this
// is Graviti alone, for behaviour that must apply to Graviti and to no other org.
export const isGravitiOrg = (orgId, orgName) => {
  const orgIdKey = getComparableOrgId(orgId);
  const orgNameKey = getComparableOrgId(orgName);

  return ['graviti', 'gravity pharma'].includes(orgIdKey)
    || ['graviti', 'gravity pharma'].includes(orgNameKey);
};

export const usesGravitiDashboardStatusRules = (orgId, orgName) => isGravitiOrg(orgId, orgName);

// The realtime notification bell (floating buzzer + live-alert drawer, rendered in AppLayout) is hidden
// for Graviti only — its icon was flagged during Graviti's FDA audit. Every other org keeps it unchanged.
export const showsNotificationBell = (orgId, orgName) => !isGravitiOrg(orgId, orgName);

export const getPasswordExpiryDays = (orgId, settings = {}) => {
  if (usesGravitiPortalFeatures(orgId)) return Number(settings?.passwordExpiryDays || 60);
  return Number(settings?.passwordExpiryDays || 0);
};

export const canUseDailyEventsReport = (orgId, settings = {}) => (
  usesGravitiPortalFeatures(orgId) || orgId === 'jwllogic' || Boolean(settings?.eventsGroupedReport)
);

// The "monitor" access permission (restricted read) is a JWL-only feature. No other org
// should see or be able to grant the monitor checkbox in access management.
export const canUseMonitorAccess = (orgId) => getComparableOrgId(orgId) === 'jwllogic';

export const canToggleUserStatus = (orgId, settings = {}) => (
  usesGravitiPortalFeatures(orgId) || Boolean(settings?.disableUserFeature || Number(settings?.maxWrongAttempts))
);

export const userHasOrgRole = (groups, orgId, roles = []) => {
  const orgIdKey = getComparableOrgId(orgId);
  if (!orgIdKey) return false;

  return (groups || []).some((groupName) => {
    const groupNameKey = getComparableOrgId(groupName);
    if (!groupNameKey.startsWith(orgIdKey)) return false;
    return roles.some((role) => {
      const roleKey = getComparableOrgId(role);
      return groupNameKey.endsWith(roleKey)
        || groupNameKey.endsWith(`${roleKey}s`);
    });
  });
};

export const userIsOrgOperator = (groups, orgId) => userHasOrgRole(groups, orgId, ['operator']);

export const userIsOrgSupervisorOrOperator = (groups, orgId) => (
  userHasOrgRole(groups, orgId, ['supervisor', 'operator'])
);

// Sensor readings and threshold ranges on the dashboard are tinted red/mustard whenever the asset's
// event aggregate carries a violation/warning — which covers the WHOLE shipment, so a reading that is
// back inside its band still shows red long after the excursion ended. KEIMED asked for that off:
// every sensor value and threshold range renders in the normal text colour, regardless of events.
// The offline ("not updated") indicator is untouched — it reports the logger, not a threshold.
export const ORGS_WITHOUT_EVENT_STATUS_COLORS = ['KEIMED'];

export const usesEventStatusColors = (orgId) => (
  !ORGS_WITHOUT_EVENT_STATUS_COLORS.map(getComparableOrgId).includes(getComparableOrgId(orgId))
);
